<template>
    <div>
        <AppModal v-model="props.isOpenModal" @update:modelValue="toggleModalChangeUtility" size="xl">
            <div class="row mb-5">
                <DateChangeAuditLog :serial="device.serial" />
            </div>
            <div class="row mb-3">
                <div class="col-3">
                    <label for="active" class="form-label text-body">{{ $t('Machine Serial') }}</label>
                    <input
                        type="text"
                        name="machineSerial"
                        id="machineSerial"
                        class="form-control"
                        tabindex="-1"
                        disabled
                        v-model="device.serial"
                    />
                </div>
                <div class="col-2">
                    <label for="active" class="form-label text-body">{{ $t('From') }}</label>
                    <AppDatepicker
                        v-model="startDate"
                        :placeholder="$t('From')"
                        class="col-3"
                        format-model="YYYY-MM-DD"
                    />
                </div>
                <div class="col-2">
                    <label for="active" class="form-label text-body">{{ $t('To') }}</label>
                    <AppDatepicker
                        v-model="endDate"
                        :placeholder="$t('To')"
                        class="col-3"
                        format-model="YYYY-MM-DD"
                    />
                </div>
                <div class="col-2 d-flex align-items-end radio-group">
                    <div class="form-check mx-2">
                        <input
                            type="radio"
                            name="type"
                            id="type-qr"
                            class="form-check-input"
                            value="qr"
                            v-model="type"
                        >
                        <label for="type-qr" class="form-check-label">QR+</label>
                    </div>
                    <div class="form-check mx-2">
                        <input
                            type="radio"
                            name="type"
                            id="type-wireless"
                            class="form-check-input"
                            value="wireless"
                            v-model="type"
                        >
                        <label for="type-wireless" class="form-check-label">{{ $t('Wireless') }}</label>
                    </div>
                </div>
                <div class="col-3 d-flex align-items-end">
                    <AppButton type="button" class="btn btn-primary me-2" @click="search">
                        {{ $t('Search') }}
                    </AppButton>
                    <AppButton type="button" btnClass="btn-white" @click="reset">
                        {{ $t('Reset') }}
                    </AppButton>
                </div>

            </div>

            <div class="row mb-5">
                <div class="col-8 d-flex align-items-end">
                    <div class="me-2">
                        <label for="active" class="form-label text-body">{{ $t('Wireless') }}</label>
                        <input
                            type="number"
                            name="wireless"
                            id="wireless"
                            class="form-control"
                            tabindex="-1"
                            v-model="wireless"
                        />
                    </div>
                    <span class="me-4">days</span>
                    <div class="d-flex align-items-end">
                        <AppButton type="button" class="btn btn-primary me-2" @click="calculate" :disabled="rows === null">
                            {{ $t('Calculate') }}
                        </AppButton>
                        <AppButton type="button" class="btn btn-primary me-2" @click="save" :disabled="saveButtonBlocked || rows === null">
                            {{ $t('Save') }}
                        </AppButton>
                        <AppButton v-if="showResetRecordNo" type="button" btnClass="btn-white" @click="resetRecordNo">
                            {{ $t('Reset Record No.') }}
                        </AppButton>
                    </div>
                </div>
                <div class="col-4 d-flex align-items-end" v-if="showErrorText">
                    <span class="highlight-duplicate p-2">{{ $t('Cannot save due to more than 1 error below') }}</span>
                </div>
            </div>

            <vue3-datatable :rows="rows"
                            :columns="columns"
                            :loading="loading"
                            :totalRows="total_rows"
                            :isServerMode="true"
                            :pageSize="params.pagesize"
                            :pageSizeOptions="[25, 50, 100, 200, 500]"
                            :sortable="true"
                            :sortColumn="params.sort_column"
                            :sortDirection="params.sort_direction"
                            @change="changeServer">
                <template #id="data">
                    <strong>#{{ data.value.id }}</strong>
                </template>
                <template #new_usage_date="data">
                    <div>
                        <input type="text"
                               ref="editInput"
                               readonly="readonly"
                               :value="data.value.editedDate || data.value.new_usage_date"
                               :class="inputClasses(data)"
                        >
                    </div>
                </template>
                <template #created="data">
                    <div>{{ formatDateTime(data.value.created) }}</div>
                </template>
                <template #treat_minutes="data">
                    <div>{{ minutesToFormattedString(data.value.treat_minutes) }}</div>
                </template>
                <template #actions="data">
                    <div class="flex gap-4">
                        <a type="button" @click="requestEditDays(data.value)">{{ $t('Edit') }}</a>
                    </div>
                </template>
            </vue3-datatable>
        </AppModal>
    </div>
</template>

<script setup>
import Vue3Datatable from '@bhplugin/vue3-datatable';
import AppModal from "@/vue/shared/AppModal.vue";
import AppButton from "@/vue/shared/AppButton.vue";
import { computed, ref } from "vue";
import {trans as $t} from "@/vue/composables/missingTranslations";
import useDates from "@/vue/composables/useDates";
import AppDatepicker from "@/vue/shared/AppDatepicker.vue";
import { useStore } from "@/vue/provideGlobalStoreState";
import {toast} from "vue3-toastify";
import usePermissions from "@/vue/composables/usePermissions";
import { useUserStore } from "@/vue/store/userStore";
import DateChangeAuditLog from "@/vue/connect/devices/tabs/usage/DateChangeAuditLog.vue";

const props = defineProps({
    device: {
        type: Object,
        required: true,
    },
    isOpenModal: {
        type: Boolean,
        required: true
    }
});

const loading = ref(false);
const params = ({
    current_page: 1,
    pagesize: 500,
    sort_column: 'created',
    sort_direction: 'desc',
});

const emits = defineEmits(['update:isOpenModal']);

const rows = ref(null);
const total_rows = ref(0);
const type = ref('');
const showResetRecordNo = ref(false);
const wireless = ref(0);
const showErrorText = ref(false);
const saveButtonBlocked = ref(false);
const duplicateIds = ref([]);
const editDays = ref(0);

const { hasPermission } = usePermissions();
const userStore = useUserStore();
const auth_user = computed(() => userStore.user);

const inputClasses = computed(() => {
    return (data) => {
        const isDuplicate = duplicateIds.value && duplicateIds.value.some(item => item.id === data.value.id);

        return {
            'highlight-duplicate': isDuplicate,
            'highlight-editing': data.value.editing
        };
    };
});

const toggleModalChangeUtility = (value) => {
    emits('update:isOpenModal', value);
};

const { getFormattedDate, minutesToFormattedString } = useDates("MM/DD/YYYY");
const { formatDateTime } = useDates("MM/DD/YYYY H:m:s");

const { usageDateRange, updateUsageDateRange } = useStore();
const startDate = computed({
    get: () => usageDateRange.value.start || null,
    set: (val) => updateUsageDateRange({ start: val || null })
});
const endDate = computed({
    get: () => usageDateRange.value.end || null,
    set: (val) => updateUsageDateRange({ end: val || null })
});

const columns = computed(() => {
    return (type.value === "wireless")
        ? [
            { field: 'id', title: 'ID', isUnique: true, hide: true },
            { field: 'record_date', title: $t('Current Usage Date'), hide: false },
            { field: 'new_usage_date', title: $t('New Usage Date'), hide: false },
            { field: 'record_no', title: $t('Record No.'), hide: false },
            { field: 'created', title: $t('Created Date'), hide: false },
            { field: 'treat_minutes', title: $t('Usage Time'), hide: false },
            { field: 'actions', title: $t('Actions'), hide: false },
        ]
        : [
            { field: 'id', title: 'ID', isUnique: true, hide: true },
            { field: 'record_date', title: $t('Current Usage Date'), hide: false },
            { field: 'new_usage_date', title: $t('New Usage Date'), hide: false },
            { field: 'created', title: $t('Created Date'), hide: false },
            { field: 'treat_minutes', title: $t('Usage Time'), hide: false },
            { field: 'actions', title: $t('Actions'), hide: false },
        ];
});

const getData = async () => {
    try {
        loading.value = true;

        const urlParams = new URLSearchParams()

        urlParams.append("page", params.current_page);
        urlParams.append("limit", params.pagesize);
        urlParams.append("orderBy", params.sort_column);
        urlParams.append("orderDir", params.sort_direction);

        if (startDate.value !== null && endDate.value !== null) {
            urlParams.append("startDate", startDate.value);
            urlParams.append("endDate", endDate.value);
        }

        const response = await axios.get(`/api/machines/${props.device.id}/usage/date-change-utility/${type.value}?${urlParams}`);

        rows.value = response.data?.data;
        total_rows.value = response?.data?.total;
    } catch {}

    loading.value = false;
};

const resetRecordNo = async () => {
    if (confirm($t("Are you sure you want to reset the record number for the last usage record?"))) {
        if (rows.value && rows.value.length > 0) {
            const stateOneRows = rows.value.filter(row => row.state === 1);

            const latestRow = stateOneRows.reduce((latest, current) => {
                const latestCreated = new Date(latest.created);
                const currentCreated = new Date(current.created);

                return currentCreated > latestCreated ? current : latest;
            }, stateOneRows[0]);

            try {
                const response = await axios.post(`/api/machines/${props.device.id}/usage/date-change-utility/wireless/reset-record-no`, {
                    'row': latestRow,
                });

                await getData();

                displayToast(true, response.data.result);
            } catch (error) {
                displayToast(false, $t("Record number was not reset"));
            }
        }
    }
};

function search() {
    if (type.value === '') {
        alert($t("You must select a source \"Wireless\" or \"QR+\""));
    }

    if (type.value === 'wireless') {
        showResetRecordNo.value = true;
    } else {
        showResetRecordNo.value = false;
    }

    getData();
}

function reset() {
    startDate.value = null;
    endDate.value = null;
    saveButtonBlocked.value = false;
    showErrorText.value = false;
    wireless.value = 0;

    getData();
}

const changeServer = (data) => {
    params.current_page = data.current_page;
    params.pagesize = data.pagesize;
    params.sort_column = data.sort_column;
    params.sort_direction = data.sort_direction;

    getData();
};

const calculate = () => {
    if (wireless.value === 0) {
        alert($t("Wireless value must be greater or less than 0"));
        return;
    }

    const daysToAdd = parseInt(wireless.value);

    rows.value.forEach((row) => {
        const originalDate = new Date(row.record_date);
        const modifiedDaysToAdd = daysToAdd > 0 ? daysToAdd : -daysToAdd;

        const newUsageDate = new Date(Date.UTC(
            originalDate.getUTCFullYear(),
            originalDate.getUTCMonth(),
            originalDate.getUTCDate() + (daysToAdd >= 0 ? modifiedDaysToAdd : -modifiedDaysToAdd)
        ));

        const month = (newUsageDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = newUsageDate.getUTCDate().toString().padStart(2, '0');
        const year = newUsageDate.getUTCFullYear();

        const formattedDate = `${year}-${month}-${day}`;

        row.new_usage_date = row.editedDate || formattedDate;
    });

    checkIfDuplicateDate();

    if (duplicateIds.value.length > 2) {
        showErrorText.value = true;
        saveButtonBlocked.value = true;
    }
};

const requestEditDays = (row) => {
    const userInput = prompt($t("Enter the number of days to add or subtract:"));
    if (userInput !== null) {
        const days = parseInt(userInput);
        if (!isNaN(days)) {
            editDays.value = days;
            edit(row);
        } else {
            alert($t("Invalid input. Please enter a valid number of days."));
        }
    }
};

const checkIfDuplicateDate = () => {
    const duplicatedDates = ref({});
    const tripleDuplicateDates = [];

    rows.value.forEach((row, index) => {
        const isDuplicate = rows.value.some((r, i) => i !== index && (r.editedDate || r.new_usage_date) === (row.editedDate || row.new_usage_date));

        if (row.editedDate || row.new_usage_date) {
            const dateKey = row.editedDate || row.new_usage_date;

            if (isDuplicate) {
                duplicatedDates.value[dateKey] = (duplicatedDates.value[dateKey] || 0) + 1;
            }

            const existingDuplicateIndex = duplicateIds.value.findIndex((item) => item.id === row.id);
            if (existingDuplicateIndex !== -1) {
                duplicateIds.value.splice(existingDuplicateIndex, 1);
            }

            if (!duplicateIds.value.some((item) => item.id === row.id && item.new_usage_date === dateKey)) {
                duplicateIds.value.push({ id: row.id, new_usage_date: dateKey });
            }
        }
    });

    duplicateIds.value = duplicateIds.value.filter(item => {
        const dateKey = item.new_usage_date;
        const dateOccurrences = duplicatedDates.value[dateKey] || 0;
        return dateOccurrences >= 3;
    });

    Object.entries(duplicatedDates.value).forEach(([date, count]) => {
        if (count >= 3) {
            tripleDuplicateDates.push(date);
        }
    });

    showErrorText.value = tripleDuplicateDates.length > 0;
    saveButtonBlocked.value = showErrorText.value;
};


const save = async  () => {
    if (confirm($t("Are you sure you want to write new usage date for all rows?"))) {
        try {
            const response = await axios.post(`/api/machines/${props.device.id}/usage/date-change-utility/${type.value}`, {
                'rows': rows.value,
            });

            getData();

            displayToast(true, response.data.result);
        } catch (error) {
            displayToast(false, $t("Date was not changed"));
        }
    }
};

const displayToast = (success, msg) => {
    if (success) {
        toast.success(msg, {
            position: 'bottom-center'
        });
    } else {
        toast.error(msg, {
            position: 'bottom-center'
        });
    }
}

const edit = (row) => {
    row.editing = true;

    const originalDate = new Date(row.record_date);
    const modifiedDaysToAdd = editDays.value > 0 ? editDays.value : -editDays.value;

    const currentDate = new Date(Date.UTC(
        originalDate.getUTCFullYear(),
        originalDate.getUTCMonth(),
        originalDate.getUTCDate() + (editDays.value >= 0 ? modifiedDaysToAdd : -modifiedDaysToAdd)
    ));

    const month = (currentDate.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = currentDate.getUTCDate().toString().padStart(2, '0');
    const year = currentDate.getUTCFullYear();

    row.editedDate = `${year}-${month}-${day}`;

    checkIfDuplicateDate();

    editDays.value = 0;
};

</script>

<style scoped>
.highlight-duplicate {
    color: red;
}
.highlight-editing {
    border: 2px solid #b3d4fc;
}
</style>
